import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { LngLat } from '@okmaps/shared';

/**
 * Distance and area measurement.
 *
 * The vertices are the single source of truth and the only input the map side
 * takes. Both ways of building a measurement - drawing with a pointer and
 * typing coordinates - converge on this array, which is what makes the keyboard
 * path a real equivalent rather than a second implementation.
 *
 * Results flow the other way and never write back to `vertices`, so there is no
 * cycle to guard against: the effect that drives the map depends on the
 * vertices alone, and a computed result cannot re-enter it.
 *
 * Every number here is in meters or square meters. The geodetic operators can
 * return any unit themselves, but they are asked for meters only so that the
 * store holds one canonical value, switching units costs nothing, and the
 * conversion factors live in one pure, tested module.
 */
export type MeasureTool = 'distance' | 'area';

export type UnitSystem = 'us' | 'metric';

export type LengthUnit = 'feet' | 'chains' | 'miles' | 'meters' | 'kilometers';

export type AreaUnit =
  | 'square-feet'
  | 'acres'
  | 'square-miles'
  | 'hectares'
  | 'square-kilometers';

/**
 * Whether the geometry engine is usable.
 *
 * The geodetic operators load a WebAssembly projection engine on first use. It
 * is tracked in state because a measurement tool that silently reports zero is
 * worse than one that says it cannot measure.
 */
export type EngineStatus = 'idle' | 'loading' | 'ready' | 'unavailable';

export interface MeasureResult {
  /** Path length for a distance measurement, perimeter for an area one. */
  readonly lengthMeters: number;
  /** Null when measuring distance. */
  readonly areaSquareMeters: number | null;
  readonly segmentMeters: readonly number[];
  /**
   * A ring that crosses itself has no meaningful area. It is reported rather
   * than silently repaired, because simplifying would produce a believable
   * number for a shape the user did not draw.
   */
  readonly selfIntersecting: boolean;
}

export interface MeasureState {
  /** Null means a map click belongs to identify, not to measurement. */
  readonly tool: MeasureTool | null;
  readonly vertices: readonly LngLat[];
  readonly result: MeasureResult | null;
  /**
   * The running value while the pointer is still moving. Kept apart from
   * `result` so the panel can tick live while the screen reader is told the
   * total once per committed vertex instead of ten times a second.
   */
  readonly preview: MeasureResult | null;
  readonly system: UnitSystem;
  readonly lengthUnit: LengthUnit | 'auto';
  readonly areaUnit: AreaUnit | 'auto';
  readonly engine: EngineStatus;
}

/**
 * US customary by default. The audience is Oklahoma surveyors, assessors and
 * county GIS staff, who work in feet, acres and sections.
 */
const initialState: MeasureState = {
  tool: null,
  vertices: [],
  result: null,
  preview: null,
  system: 'us',
  lengthUnit: 'auto',
  areaUnit: 'auto',
  engine: 'idle',
};

/** Immer's draft type strips `readonly`, so vertices are copied element-wise. */
function copyVertices(vertices: readonly LngLat[]): LngLat[] {
  return vertices.map((v) => ({ lng: v.lng, lat: v.lat }));
}

/** Same reason: the segment list is readonly and the draft type is not. */
function copyResult(result: MeasureResult) {
  return { ...result, segmentMeters: [...result.segmentMeters] };
}

const measureSlice = createSlice({
  name: 'measure',
  initialState,
  reducers: {
    /**
     * Switching tools discards the measurement, because a polygon's vertices
     * are not a sensible polyline and the reverse is worse. Choosing the tool
     * that is already active is a no-op, so a stray re-dispatch cannot wipe
     * work in progress.
     */
    toolSelected(state, action: PayloadAction<MeasureTool>) {
      if (state.tool === action.payload) return;
      state.tool = action.payload;
      state.vertices = [];
      state.result = null;
      state.preview = null;
    },
    /** Leave measurement entirely; map clicks go back to identify. */
    toolCleared(state) {
      state.tool = null;
      state.vertices = [];
      state.result = null;
      state.preview = null;
    },
    /** Keep the tool active but start the measurement over. */
    measurementCleared(state) {
      state.vertices = [];
      state.result = null;
      state.preview = null;
    },
    /** The whole vertex list, as reported by the sketch. */
    verticesChanged(state, action: PayloadAction<readonly LngLat[]>) {
      state.vertices = copyVertices(action.payload);
      state.preview = null;
    },
    /** One vertex from the coordinate entry, which is the keyboard path. */
    vertexAppended(state, action: PayloadAction<LngLat>) {
      state.vertices.push({ lng: action.payload.lng, lat: action.payload.lat });
      state.preview = null;
    },
    /** Out-of-range indices are ignored rather than corrupting the list. */
    vertexRemoved(state, action: PayloadAction<number>) {
      if (action.payload < 0 || action.payload >= state.vertices.length) return;
      state.vertices.splice(action.payload, 1);
      state.preview = null;
    },
    /**
     * Computed from the committed vertices. Deliberately does not touch
     * `vertices` - that is what keeps the map effect from re-entering itself.
     */
    resultComputed(state, action: PayloadAction<MeasureResult | null>) {
      state.result = action.payload ? copyResult(action.payload) : null;
      state.preview = null;
    },
    /** High frequency during a drag; suppressed in the action logger. */
    previewUpdated(state, action: PayloadAction<MeasureResult | null>) {
      state.preview = action.payload ? copyResult(action.payload) : null;
    },
    unitSystemChanged(state, action: PayloadAction<UnitSystem>) {
      state.system = action.payload;
      // The explicit overrides belong to the system they were chosen in;
      // carrying "acres" into metric would be nonsense.
      state.lengthUnit = 'auto';
      state.areaUnit = 'auto';
    },
    lengthUnitChanged(state, action: PayloadAction<LengthUnit | 'auto'>) {
      state.lengthUnit = action.payload;
    },
    areaUnitChanged(state, action: PayloadAction<AreaUnit | 'auto'>) {
      state.areaUnit = action.payload;
    },
    engineStatusChanged(state, action: PayloadAction<EngineStatus>) {
      state.engine = action.payload;
    },
  },
});

export const {
  toolSelected,
  toolCleared,
  measurementCleared,
  verticesChanged,
  vertexAppended,
  vertexRemoved,
  resultComputed,
  previewUpdated,
  unitSystemChanged,
  lengthUnitChanged,
  areaUnitChanged,
  engineStatusChanged,
} = measureSlice.actions;

export default measureSlice.reducer;
